import { supabase } from './supabase';
import { netlifyFunctionsBase } from '../utils/netlifyFunctionsBase';

async function authHeaders(): Promise<HeadersInit> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Debes iniciar sesión');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

async function feFetch<T = unknown>(
  path: string,
  init: RequestInit & { method?: string } = {}
): Promise<T> {
  const headers = await authHeaders();
  const res = await fetch(`${netlifyFunctionsBase()}/${path}`, {
    ...init,
    headers: { ...headers, ...(init.headers || {}) },
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json?.ok === false) {
    const detail =
      json?.error ||
      json?.message ||
      (json?.matias && (json.matias.message || JSON.stringify(json.matias))) ||
      `Error ${res.status}`;
    throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
  }
  return json as T;
}

export type EmpresaFeConfig = {
  empresa_id: string;
  enabled: boolean;
  ambiente: 'sandbox' | 'produccion';
  estado: 'inactivo' | 'pendiente' | 'activo' | 'error';
  matias_client_uuid?: string | null;
  matias_client_id?: number | null;
  nit?: string | null;
  razon_social?: string | null;
  email?: string | null;
  contact_first_name?: string | null;
  contact_last_name?: string | null;
  address?: string | null;
  mobile?: string | null;
  resolution_number?: string | null;
  resolution_prefix?: string | null;
  certificate_name?: string | null;
  last_error?: string | null;
  activated_at?: string | null;
};

export async function getFeEstado(empresaId: string, refresh = false) {
  const q = new URLSearchParams({
    empresa_id: empresaId,
    ...(refresh ? { refresh: '1' } : {}),
  });
  return feFetch<{
    ok: true;
    config: EmpresaFeConfig | null;
    server: { matias_ready: boolean; ambiente: string };
    refresh_error?: string;
  }>(`fe-matias-estado?${q.toString()}`, { method: 'GET' });
}

export async function activarFe(payload: {
  empresa_id: string;
  nit: string;
  razon_social: string;
  email: string;
  contact_first_name: string;
  contact_last_name: string;
  address?: string;
  mobile?: string;
  password?: string;
}) {
  return feFetch<{
    ok: true;
    message: string;
    config: EmpresaFeConfig;
    reused?: boolean;
    matias_email?: string;
    temporary_password?: string;
  }>('fe-matias-activar', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function desactivarFe(empresaId: string) {
  return feFetch<{ ok: true; message: string; config: EmpresaFeConfig | null }>(
    'fe-matias-desactivar',
    {
      method: 'POST',
      body: JSON.stringify({ empresa_id: empresaId }),
    }
  );
}

export async function emitirFacturaFe(empresaId: string, facturaId: string) {
  return feFetch<{
    ok: boolean;
    fe_estado?: string;
    fe_cufe?: string | null;
    fe_track_id?: string | null;
    fe_error?: string | null;
    already_emitted?: boolean;
  }>('fe-matias-emitir', {
    method: 'POST',
    body: JSON.stringify({ empresa_id: empresaId, factura_id: facturaId }),
  });
}

/** Client-side read (RLS) — used to decide auto-emit without calling Netlify. */
export async function loadFeConfigLocal(empresaId: string): Promise<EmpresaFeConfig | null> {
  const { data, error } = await supabase
    .from('empresa_facturacion_electronica')
    .select('*')
    .eq('empresa_id', empresaId)
    .maybeSingle();
  if (error) {
    // Table may not exist yet pre-migration
    console.warn('[FE]', error.message);
    return null;
  }
  return data as EmpresaFeConfig | null;
}
